/**
 * Notification channels — where alerts actually go.
 *
 * Deliberately wider than "email": routing can already target webhooks and
 * SMS, so a screen that could only manage email would leave two of the three
 * kinds creatable nowhere.
 *
 * ── Verified means verified ──────────────────────────────────────────────
 *
 * 🔴 The status column is the point of the screen. `verified_at` had one writer
 * — the seed — so every channel a user created read "· unverified" for ever on
 * the routing screen, and the label decayed from a warning into decoration. The
 * Verify button sends a real message through the real transport and the stamp
 * moves only if that succeeded. A dead-man's switch once wrote CRITICAL for 59
 * consecutive nights to a host with no mail server and nothing could tell; this
 * is the button that would have told.
 *
 * Three states are drawn differently on purpose, because two of them are
 * routinely conflated:
 *
 *   verified    a message left the building and was accepted
 *   never       nobody has tried
 *   failed      somebody tried and it did not work — `lastError` says how
 *
 * ── Saving can un-verify, and says so before you press it ────────────────
 *
 * Changing what a channel points at clears its verification, because a proof
 * about one address is not a proof about another. The drawer says which pending
 * edits will do that rather than letting the badge change afterwards and look
 * like a bug.
 *
 * ── Two refusals are drawn, not hidden ───────────────────────────────────
 *
 * A channel a routing rule still targets cannot be deleted, and the rules are
 * named. A kind this deployment has no transport for cannot be verified, and
 * the button says why. Both follow `Routing.tsx`: "I cannot find the button"
 * and "this must not be done" are different things to learn.
 *
 * ── Shape ────────────────────────────────────────────────────────────────
 *
 * A read-only table, one row per channel; the row opens a drawer holding the
 * edit form. Verify is the one action that stays on the row, because it is a
 * test of the transport rather than an edit. New channel is the page's single
 * primary action and also opens a drawer.
 */

import { Plus } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';
import { verifyOutcome } from './verify-outcome.ts';

type Kind = 'email' | 'webhook' | 'sms' | 'slack' | 'teams';

const KINDS: Kind[] = ['email', 'webhook', 'sms', 'slack', 'teams'];

/** Which kinds carry a recipient list rather than a url. */
const ADDRESSED: ReadonlySet<Kind> = new Set<Kind>(['email', 'sms']);

interface Channel {
  id: string;
  label: string;
  kind: Kind;
  config: { to?: string[]; url?: string };
  hasSecret: boolean;
  enabled: boolean;
  verifiedAt: string | null;
  lastError: string | null;
  createdAt: string;
  usedBy: string[];
  verifiable: boolean;
}

interface ChannelsResponse {
  channels: Channel[];
  sendableKinds: Kind[];
}

/** What a channel is being edited into, before it is saved. */
interface Draft {
  label: string;
  kind: Kind;
  /** Comma or newline separated on screen; an array on the wire. */
  recipients: string;
  url: string;
  secret: string;
  clearSecret: boolean;
  enabled: boolean;
}

function draftOf(channel: Channel): Draft {
  return {
    label: channel.label,
    kind: channel.kind,
    recipients: (channel.config.to ?? []).join(', '),
    url: channel.config.url ?? '',
    secret: '',
    clearSecret: false,
    enabled: channel.enabled,
  };
}

/**
 * A typed list into the array the server wants.
 *
 * Split on commas, semicolons and whitespace, because every one of those is
 * something a person pastes. The server is the authority on whether each piece
 * is an address — this only has to stop turning one line into one recipient.
 */
function recipientsOf(text: string): string[] {
  return text
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

function configOf(draft: Draft): Record<string, unknown> {
  if (ADDRESSED.has(draft.kind)) return { to: recipientsOf(draft.recipients) };
  return {
    url: draft.url,
    // Blank means unchanged and null means remove — the screen cannot display
    // the stored secret, so it must not be able to delete it by saving.
    secret: draft.clearSecret ? null : draft.secret,
  };
}

/** True when saving this draft would clear the channel's verification. */
function wouldUnverify(channel: Channel, draft: Draft): boolean {
  if (channel.verifiedAt === null) return false;
  if (draft.kind !== channel.kind) return true;
  if (ADDRESSED.has(draft.kind)) {
    return recipientsOf(draft.recipients).join(' ') !== (channel.config.to ?? []).join(' ');
  }
  if (draft.url !== (channel.config.url ?? '')) return true;
  return draft.clearSecret || draft.secret !== '';
}

function describeKey(key: string): string {
  if (key === '*') return 'the default';
  return key;
}

function describeKind(kind: Kind): string {
  return `${kind === 'email' ? 'an' : 'a'} ${kind}`;
}

/** What the channel points at, as one line for a table cell. */
function destinationOf(channel: Channel): string {
  if (ADDRESSED.has(channel.kind)) return (channel.config.to ?? []).join(', ');
  return channel.config.url ?? '';
}

function messageOf(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

export function ChannelsView(): React.JSX.Element {
  const [data, setData] = useState<ChannelsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** The channel whose Verify is in flight — the row's button is disabled, nothing else. */
  const [verifying, setVerifying] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<ChannelsResponse>('/api/channels');
      setData(next);
      setError(null);
    } catch (err) {
      setError(messageOf(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const verify = useCallback(
    async (channel: Channel) => {
      setVerifying(channel.id);
      setError(null);
      setNote(null);

      let caught: unknown = null;
      try {
        await api.post(`/api/channels/${channel.id}/verify`);
      } catch (err) {
        caught = err;
      }

      /*
       * Reload BEFORE saying what happened. `load` clears the error banner when
       * it succeeds, so a message set first is wiped by the very refresh that
       * fetches the new state — which is what happened here: the row went to
       * "failed" with its reason and the banner explaining it vanished in the
       * same paint. Found by pressing the button, not by a test.
       */
      await load();
      setVerifying(null);

      /*
       * Not swallowed and not silently retried. A verification that failed is
       * the single most useful thing this screen can say — and "nothing was
       * attempted" must not be dressed as one, which is what happened until the
       * decision moved somewhere it could be tested.
       */
      const outcome = verifyOutcome(channel.label, caught);
      if (outcome.kind === 'failed') setError(outcome.text);
      else setNote(outcome.text);
    },
    [load],
  );

  const sendable = useMemo(() => new Set<Kind>(data?.sendableKinds ?? []), [data]);

  const columns = useMemo<ColumnDef<Channel, unknown>[]>(
    () => [
      {
        id: 'label',
        header: 'Name',
        accessorKey: 'label',
        cell: ({ row }) => (
          <span className="row">
            <strong>{row.original.label}</strong>
            {row.original.enabled ? null : <Pill state="neutral">disabled</Pill>}
          </span>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        accessorKey: 'kind',
        cell: ({ row }) => (
          <span className="row">
            {row.original.kind}
            {sendable.has(row.original.kind) ? null : (
              <Pill
                state="unmonitored"
                reason={`Nothing in this deployment can send ${describeKind(row.original.kind)} message. The channel can be targeted, but nothing will be sent to it and it can never be verified.`}
              >
                no transport
              </Pill>
            )}
          </span>
        ),
        size: 140,
      },
      {
        id: 'to',
        header: 'Sends to',
        accessorFn: destinationOf,
        enableSorting: false,
        cell: ({ getValue }) => {
          const to = getValue() as string;
          return to === '' ? <span className="muted">—</span> : <span className="wrap-any">{to}</span>;
        },
      },
      {
        id: 'verification',
        header: 'Verification',
        accessorFn: (c) => (c.verifiedAt !== null ? 2 : c.lastError !== null ? 0 : 1),
        cell: ({ row }) => <VerifyCell channel={row.original} busy={verifying === row.original.id} onVerify={verify} />,
        size: 200,
      },
      {
        id: 'usedBy',
        header: 'Used by',
        accessorFn: (c) => c.usedBy.length,
        cell: ({ row }) =>
          row.original.usedBy.length === 0 ? (
            <span className="muted">no rule</span>
          ) : (
            <span className="muted">{row.original.usedBy.map(describeKey).join(', ')}</span>
          ),
      },
    ],
    [sendable, verify, verifying],
  );

  if (error !== null && data === null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={4} cols={5} label="Loading channels" />;

  const current = selected === null ? undefined : data.channels.find((c) => c.id === selected);

  return (
    <>
      <Toolbar
        end={
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>
            New channel
          </Button>
        }
      />

      {error !== null && <Banner tone="fault">{error}</Banner>}
      {note !== null && (
        <Banner
          tone="ok"
          actions={
            <Button variant="ghost" size="sm" onClick={() => setNote(null)}>
              Dismiss
            </Button>
          }
        >
          {note}
        </Banner>
      )}

      <Section
        flush
        title="Notification channels"
        summary="Verify sends a real message through the channel; the badge moves only if it arrived."
      >
        <DataTable
          data={data.channels}
          columns={columns}
          getRowId={(c) => c.id}
          onRowClick={(c) => setSelected(c.id)}
          rowClass={(c) => (c.enabled ? undefined : 'row-muted')}
          initialSort={[{ id: 'label', desc: false }]}
          caption="Notification channels"
          empty={
            <Empty
              title="No channels"
              body="Every alert this tenant raises currently reaches nobody, whatever the routing says."
            />
          }
        />
      </Section>

      {current !== undefined && (
        <ChannelDrawer
          key={current.id}
          channel={current}
          sendable={sendable}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}

      <NewChannelDrawer
        open={creating}
        sendable={sendable}
        onClose={() => setCreating(false)}
        onCreated={load}
      />
    </>
  );
}

/**
 * The verification cell: the pill, the sentence behind it, and the button.
 *
 * The button stops the click reaching the row, which would otherwise open the
 * drawer on top of a verification that is already in flight.
 */
function VerifyCell({
  channel,
  busy,
  onVerify,
}: {
  channel: Channel;
  busy: boolean;
  onVerify: (channel: Channel) => Promise<void>;
}): React.JSX.Element {
  const pill =
    channel.verifiedAt !== null ? (
      <Pill state="ok" reason={`Verified ${new Date(channel.verifiedAt).toLocaleString()}`}>
        verified
      </Pill>
    ) : channel.lastError !== null ? (
      <Pill state="fault" reason={channel.lastError}>
        failed
      </Pill>
    ) : (
      <Pill state="stale" reason="Nobody has tried to reach this channel.">
        never
      </Pill>
    );

  return (
    <div className="stack-tight">
      {pill}
      <Button
        size="sm"
        busy={busy}
        disabled={!channel.verifiable}
        title={
          channel.verifiable
            ? 'Send a real test message through this channel.'
            : `Nothing in this deployment can send ${describeKind(channel.kind)} message, so a test would prove nothing.`
        }
        onClick={(e) => {
          e.stopPropagation();
          void onVerify(channel);
        }}
      >
        Verify
      </Button>
    </div>
  );
}

/**
 * The shared body of both drawers: what a channel is called, what kind it is,
 * where it goes. The edit drawer adds the secret-removal tick and the enabled
 * tick; the create drawer does not need them.
 */
function ChannelFields({
  idPrefix,
  draft,
  sendable,
  hasSecret,
  busy,
  onEdit,
}: {
  idPrefix: string;
  draft: Draft;
  sendable: ReadonlySet<Kind>;
  /** The channel already holds a secret the screen cannot show. */
  hasSecret: boolean;
  busy: boolean;
  onEdit: (patch: Partial<Draft>) => void;
}): React.JSX.Element {
  const addressed = ADDRESSED.has(draft.kind);
  const secretId = `${idPrefix}-clear-secret`;
  return (
    <>
      <Field id={`${idPrefix}-label`} label="Name">
        <input
          id={`${idPrefix}-label`}
          value={draft.label}
          placeholder="Northwind ops email"
          disabled={busy}
          onChange={(e) => onEdit({ label: e.target.value })}
        />
      </Field>

      {/*
        Stated at the moment of choosing rather than discovered at the moment
        of delivery. `deliverAlert` records a failed notification for a kind
        with no transport, which is the right alarm and the wrong time.
      */}
      <Field
        id={`${idPrefix}-kind`}
        label="Kind"
        {...(sendable.has(draft.kind)
          ? {}
          : {
              hint: `This deployment has no transport for ${draft.kind}. A channel of this kind can be created and targeted, but nothing will be sent to it and it can never be verified.`,
            })}
      >
        <select
          id={`${idPrefix}-kind`}
          value={draft.kind}
          disabled={busy}
          onChange={(e) => onEdit({ kind: e.target.value as Kind })}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </Field>

      {addressed ? (
        <Field
          id={`${idPrefix}-to`}
          label="Recipients"
          hint="Separate several with commas or new lines."
        >
          <input
            id={`${idPrefix}-to`}
            value={draft.recipients}
            placeholder={draft.kind === 'sms' ? '+61400000000' : 'ops@example.com, duty@example.com'}
            disabled={busy}
            onChange={(e) => onEdit({ recipients: e.target.value })}
          />
        </Field>
      ) : (
        <>
          <Field id={`${idPrefix}-url`} label="URL">
            <input
              id={`${idPrefix}-url`}
              value={draft.url}
              placeholder="https://receiver.example/hook"
              disabled={busy}
              onChange={(e) => onEdit({ url: e.target.value })}
            />
          </Field>
          <Field
            id={`${idPrefix}-secret`}
            label="Signing secret (optional)"
            {...(hasSecret ? { hint: 'A secret is stored and cannot be shown. Blank leaves it unchanged.' } : {})}
          >
            <input
              id={`${idPrefix}-secret`}
              value={draft.secret}
              disabled={busy || draft.clearSecret}
              onChange={(e) => onEdit({ secret: e.target.value })}
            />
          </Field>
          {hasSecret && (
            <div className="row">
              <input
                id={secretId}
                type="checkbox"
                checked={draft.clearSecret}
                disabled={busy}
                onChange={(e) => onEdit({ clearSecret: e.target.checked })}
              />
              <label htmlFor={secretId}>Remove the signing secret</label>
            </div>
          )}
        </>
      )}
    </>
  );
}

function ChannelDrawer({
  channel,
  sendable,
  onClose,
  onChanged,
}: {
  channel: Channel;
  sendable: ReadonlySet<Kind>;
  onClose: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => draftOf(channel));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const edit = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  const inUse = channel.usedBy.length > 0;
  const unverifying = wouldUnverify(channel, draft);
  const enabledId = `channel-${channel.id}-enabled`;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/channels/${channel.id}`, {
        label: draft.label,
        kind: draft.kind,
        config: configOf(draft),
        enabled: draft.enabled,
      });
      await onChanged();
      onClose();
    } catch (err) {
      // The server refuses a channel that reaches nobody, and the reason is
      // the useful part.
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/channels/${channel.id}`);
      await onChanged();
      onClose();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title={channel.label} subtitle={`${channel.kind} channel`}>
      <div className="stack form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {unverifying && (
          /* Said before the button is pressed, not discovered after the badge
             changes. */
          <Banner tone="warn">
            Saving this will un-verify the channel: a proof about one address is not a proof
            about another.
          </Banner>
        )}

        <ChannelFields
          idPrefix={`channel-${channel.id}`}
          draft={draft}
          sendable={sendable}
          hasSecret={channel.hasSecret}
          busy={busy}
          onEdit={edit}
        />

        <div className="row">
          <input
            id={enabledId}
            type="checkbox"
            checked={draft.enabled}
            disabled={busy}
            onChange={(e) => edit({ enabled: e.target.checked })}
          />
          <label htmlFor={enabledId}>Enabled</label>
        </div>

        <div className="stack-tight">
          <span className="eyebrow">Used by</span>
          <span className="small muted">
            {inUse ? channel.usedBy.map(describeKey).join(', ') : 'no routing rule'}
          </span>
        </div>

        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={inUse}
            title={
              inUse
                ? `Still a recipient of ${channel.usedBy.join(', ')}. Remove it there first, or those alerts quietly reach nobody.`
                : 'Delete this channel.'
            }
            onClick={() => void remove()}
          >
            Delete
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

const EMPTY_DRAFT: Draft = {
  label: '',
  kind: 'email',
  recipients: '',
  url: '',
  secret: '',
  clearSecret: false,
  enabled: true,
};

function NewChannelDrawer({
  open,
  sendable,
  onClose,
  onCreated,
}: {
  open: boolean;
  sendable: ReadonlySet<Kind>;
  onClose: () => void;
  onCreated: () => Promise<void>;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const edit = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  const close = (): void => {
    setDraft(EMPTY_DRAFT);
    setError(null);
    onClose();
  };

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/channels', {
        label: draft.label,
        kind: draft.kind,
        config: ADDRESSED.has(draft.kind)
          ? { to: recipientsOf(draft.recipients) }
          : { url: draft.url, secret: draft.secret },
      });
      await onCreated();
      close();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="New channel"
      subtitle="Starts unverified; only a message that actually arrived can change that."
    >
      <div className="stack form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        <ChannelFields
          idPrefix="new-channel"
          draft={draft}
          sendable={sendable}
          hasSecret={false}
          busy={busy}
          onEdit={edit}
        />

        <div className="row">
          <Button
            variant="primary"
            busy={busy}
            disabled={draft.label.trim() === ''}
            onClick={() => void create()}
          >
            Create
          </Button>
          <Button variant="ghost" disabled={busy} onClick={close}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
